"use client";

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

import { useShell } from "@/components/shell/shell-context";
import { ApiError, api, loginUrl } from "@/lib/api/client";
import type { AuditCrawl, AuditOverview, Project } from "@/lib/api/types";
import { CATEGORIES, COLUMN_LABEL, type Category, crawlLabel, formatDelta, llmsLine, relevantMissing } from "@/lib/audit";
import { formatCount, formatDate, formatDateTime } from "@/lib/format";

import { ImportPanel } from "./import-panel";
import { IssueTable } from "./issue-table";

type Panel = "none" | "internal" | "links";

export function AuditView() {
  const { project } = useShell();
  // Bei Projektwechsel neu aufbauen: Crawls, Auswahl und Import gehören zum Projekt
  return project ? <View key={project.id} project={project} /> : null;
}

function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : "Unbekannter Fehler";
}

function Kpi({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-line bg-white px-5 py-4">
      <span className="text-[13px] text-muted">{label}</span>
      <span className="text-[26px] font-semibold tracking-tight">{value}</span>
      <span className="text-xs text-muted">{detail}</span>
    </div>
  );
}

function View({ project }: { project: Project }) {
  const router = useRouter();
  const [overview, setOverview] = useState<AuditOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel>("none");
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState<Category>("Alle");
  const [crawlId, setCrawlId] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(
    (id: number | null) =>
      api<AuditOverview>(`/api/audit/overview?project_id=${project.id}${id ? `&crawl_id=${id}` : ""}`)
        .then((next) => {
          setOverview(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (caught instanceof ApiError && caught.status === 401) {
            router.replace(loginUrl("/audit"));
            return;
          }
          setError(message(caught));
        }),
    [project.id, router],
  );

  useEffect(() => {
    void load(crawlId);
  }, [load, crawlId]);

  const crawl = overview?.crawl ?? null;

  const importFiles = async (files: { internal: File | null; links: File | null }) => {
    setBusy(true);
    setImportError(null);
    setNotice(null);
    try {
      let current: AuditCrawl | null = crawl;
      if (panel === "internal" && files.internal) {
        current = await api<AuditCrawl>(
          `/api/audit/crawls?project_id=${project.id}&filename=${encodeURIComponent(files.internal.name)}`,
          { method: "POST", file: files.internal },
        );
      }
      if (files.links && current) {
        current = await api<AuditCrawl>(
          `/api/audit/crawls/${current.id}/links?project_id=${project.id}&filename=${encodeURIComponent(files.links.name)}`,
          { method: "POST", file: files.links },
        );
      }
      setPanel("none");
      setNotice(current ? `${formatCount(current.url_count)} URLs importiert` : null);
      setCrawlId(current?.id ?? null);
      await load(current?.id ?? null);
    } catch (caught) {
      setImportError(message(caught));
      // Ein halber Import (Crawl da, Links fehlgeschlagen) soll in der Liste erscheinen
      await load(crawlId);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!crawl) return;
    setBusy(true);
    try {
      await api(`/api/audit/crawls/${crawl.id}?project_id=${project.id}`, { method: "DELETE" });
      setCrawlId(null);
      setNotice("Crawl gelöscht");
      await load(null);
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  };

  const missing = crawl ? relevantMissing(crawl.missing) : [];

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 py-7 sm:px-9">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="text-[13px] text-muted">
            Site Audit · Screaming Frog
            {crawl ? ` · Import vom ${formatDateTime(crawl.created_at)}, ${formatCount(crawl.url_count)} URLs` : " · noch kein Crawl importiert"}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Site Audit</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {crawl?.previous && (
            <span className="rounded bg-info-bg px-2 py-1 text-xs font-medium text-info">Vergleich mit {formatDate(crawl.previous.created_at)}</span>
          )}
          <button
            type="button"
            disabled
            title="Der Crawl über Firecrawl folgt. Für große Seiten läuft der Crawl in Screaming Frog."
            className="h-11 rounded-md border border-field bg-white px-3.5 text-sm text-muted"
          >
            Crawl mit Firecrawl (folgt)
          </button>
          <button
            type="button"
            aria-expanded={panel === "internal"}
            onClick={() => setPanel(panel === "internal" ? "none" : "internal")}
            className="h-11 rounded-md bg-accent px-4 text-sm font-medium text-white"
          >
            Crawl importieren
          </button>
        </div>
      </header>

      {error && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-md bg-danger-bg px-4 py-3 text-sm text-danger">
          <span>{error}</span>
          <button type="button" onClick={() => void load(crawlId)} className="h-11 rounded-md border border-danger bg-white px-3.5">
            Erneut versuchen
          </button>
        </div>
      )}
      {notice && (
        <p role="status" className="rounded-md bg-success-bg px-4 py-2 text-sm text-success">
          {notice}
        </p>
      )}

      {panel !== "none" && (
        <ImportPanel
          key={panel}
          mode={panel}
          busy={busy}
          error={importError}
          onImport={(files) => void importFiles(files)}
          onCancel={() => {
            setPanel("none");
            setImportError(null);
          }}
        />
      )}

      {overview === null && !error && <p className="text-sm text-muted">Site Audit wird geladen …</p>}

      {overview && crawl === null && panel === "none" && (
        <p className="rounded-lg border border-line bg-white px-5 py-6 text-sm text-muted">
          Noch kein Crawl importiert. Crawle {project.domain} in Screaming Frog und importiere „Internal: All“ über „Crawl importieren“. Das Audit prüft Statuscodes,
          Weiterleitungen, Canonicals, Titles, Descriptions, H1, Klicktiefe, Orphan Pages und GEO-Signale wie Textmenge und Aktualität, vergleicht mit dem
          vorigen Crawl und kostet nichts.
        </p>
      )}

      {overview && crawl && (
        <>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {overview.crawls.length > 1 && (
              <div className="flex items-center gap-2">
                <label htmlFor="audit-crawl" className="text-[13px] font-medium">
                  Crawl
                </label>
                <select
                  id="audit-crawl"
                  value={crawl.id}
                  onChange={(event) => setCrawlId(Number(event.target.value))}
                  className="h-11 max-w-[420px] rounded-md border border-field bg-white px-3 text-sm"
                >
                  {overview.crawls.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {crawlLabel(entry)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button
              type="button"
              onClick={() => setPanel(panel === "links" ? "none" : "links")}
              aria-expanded={panel === "links"}
              className="h-11 rounded-md border border-field bg-white px-3.5 text-[13px]"
            >
              {crawl.link_count > 0 ? `All Inlinks ersetzen (${formatCount(crawl.link_count)} Links)` : "All Inlinks ergänzen"}
            </button>
            <button type="button" disabled={busy} onClick={() => void remove()} className="h-11 rounded-md border border-field bg-white px-3.5 text-[13px] text-danger">
              Crawl löschen
            </button>
          </div>

          {crawl.truncated && (
            <p role="status" className="rounded-md bg-warning-bg px-4 py-3 text-sm text-warning">
              Der Export war größer als die Obergrenze (300.000 URLs, 1.000.000 Links). Das Audit zeigt nur den gelesenen Teil.
            </p>
          )}
          {missing.length > 0 && (
            <p role="status" className="rounded-md bg-warning-bg px-4 py-3 text-sm text-warning">
              Im Export fehlen Spalten: {missing.map((key) => COLUMN_LABEL[key] ?? key).join(", ")}. Die Issues, die sie brauchen, entfallen (unten unter „Nicht geprüft“).
            </p>
          )}

          <section aria-label="Crawl-Kennzahlen" className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
            <Kpi
              label="URLs gecrawlt"
              value={formatCount(crawl.kpis.urls)}
              detail={`${formatDelta(crawl.kpis.delta_urls)} seit letztem Crawl · ${formatCount(crawl.kpis.errors)} mit Fehlerstatus`}
            />
            <Kpi
              label="Indexierbar"
              value={formatCount(crawl.kpis.indexable)}
              detail={`${crawl.kpis.urls > 0 ? ((crawl.kpis.indexable / crawl.kpis.urls) * 100).toLocaleString("de-DE", { maximumFractionDigits: 1 }) : "0"} % aller URLs`}
            />
            <Kpi label="SEO-Basics-Issues" value={formatCount(crawl.kpis.seo_issues)} detail={`${formatDelta(crawl.kpis.delta_seo_issues)} ggü. Vorgänger`} />
            <Kpi label="GEO-Issues" value={formatCount(crawl.kpis.geo_issues)} detail={`${formatDelta(crawl.kpis.delta_geo_issues)} ggü. Vorgänger`} />
          </section>

          <section aria-labelledby="issues-title" className="rounded-lg border border-line bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e6e9e7] px-5 py-3">
              <h2 id="issues-title" className="text-base font-semibold">
                Issues
              </h2>
              <div role="group" aria-label="Kategorie" className="flex flex-wrap gap-1.5">
                {CATEGORIES.map((entry) => (
                  <button
                    key={entry}
                    type="button"
                    aria-pressed={category === entry}
                    onClick={() => setCategory(entry)}
                    className={`h-11 rounded-full border px-3.5 text-[13px] ${category === entry ? "border-ink bg-ink text-white" : "border-field bg-white"}`}
                  >
                    {entry}
                  </button>
                ))}
              </div>
            </div>
            <div className="px-3 py-3">
              <IssueTable key={`${crawl.id}-${category}`} crawl={crawl} projectId={project.id} category={category} />
            </div>
          </section>

          <div className="flex flex-wrap items-start gap-5">
            <section aria-labelledby="sections-title" className="flex min-w-0 flex-[1_1_420px] flex-col gap-2 overflow-x-auto rounded-lg border border-line bg-white px-5 py-4">
              <h2 id="sections-title" className="text-base font-semibold">
                Verzeichnisse
              </h2>
              <table aria-label="Verzeichnisse" className="w-full min-w-[420px] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted">
                    <th className="py-2 font-medium">Verzeichnis</th>
                    <th className="px-2 py-2 text-right font-medium">URLs</th>
                    <th className="px-2 py-2 text-right font-medium">Indexierbar</th>
                    <th className="px-2 py-2 text-right font-medium">Ø Wörter</th>
                    <th className="py-2 text-right font-medium">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {crawl.sections.map((section) => (
                    <tr key={section.section} className="border-t border-neutral-bg">
                      <td className="py-2 font-mono text-[13px]">{section.section}</td>
                      <td className="px-2 py-2 text-right font-mono text-[13px]">{formatCount(section.urls)}</td>
                      <td className="px-2 py-2 text-right font-mono text-[13px]">{formatCount(section.indexable)}</td>
                      <td className="px-2 py-2 text-right font-mono text-[13px]">{section.avg_words === null ? "–" : formatCount(section.avg_words)}</td>
                      <td className="py-2 text-right font-mono text-[13px]">{formatCount(section.issues)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {crawl.llms && <span className="text-xs text-muted">{llmsLine(crawl.llms)}</span>}
            </section>

            <section aria-labelledby="skipped-title" className="flex min-w-0 flex-[1_1_380px] flex-col gap-2 rounded-lg border border-line bg-white px-5 py-4">
              <h2 id="skipped-title" className="text-base font-semibold">
                Nicht geprüft
              </h2>
              <p className="text-xs text-muted">Diese Prüfungen liefert der Import nicht oder nicht mit diesem Export. „Kein Befund“ heißt hier nicht „in Ordnung“.</p>
              <ul className="flex flex-col gap-2 text-[13px]">
                {crawl.skipped.map((entry) => (
                  <li key={entry.key}>
                    <span className="font-medium">{entry.title}</span>
                    <span className="block text-xs text-muted">{entry.reason}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </>
      )}
    </div>
  );
}
